import {
    Box,
    Button,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    FormHelperText,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Dialog,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import SuccessSnackbarEffect from 'component/success-snackbar';
import Select from '@mui/material/Select';
import { CreateItemRequest } from 'types/toont-tint/tool-tint-item';
import { useCreateToonTintItemMutation } from 'store/slices/toon-tint/toon-tint-item';
import { selectCategoryIdOptions } from 'store/slices/toon-tint/toon-tint-category-id-slice';
import { useProductTitleSearchListQuery } from 'store/slices/toon-tint/toon-tint';
import PageviewIcon from '@mui/icons-material/Pageview';
import ViewProduct from 'component/view-product';

interface Props {
    setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: any;
}

const defaultSelected = [
    { label: 'True', value: 'true' },
    { label: 'False', value: 'false' },
];

const AddItem: React.FC<Props> = ({ setOpenDialog, refetch }) => {
    const [searchProduct, setSearchProduct] = useState('');
    const [viewProduct, setViewProduct] = useState('');
    const [openViewProductDialog, setOpenViewProductDialog] =
        React.useState(false);
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<CreateItemRequest>({
        defaultValues: {
            productItem: undefined,
            isDefault: 'false',
            itemPosition: 1,
        },
    });

    const { categoryId } = useAppSelector(selectCategoryIdOptions);

    const [sendToonTint, { error, isError, isSuccess, isLoading }] =
        useCreateToonTintItemMutation();

    const { data: dataProductToonTint, isFetching } =
        useProductTitleSearchListQuery({
            productType: 'toon-tint',
            searchText: searchProduct,
        });

    const onSubmit = (values: CreateItemRequest) => {
        sendToonTint({
            itemPosition: Number(values.itemPosition),
            isDefault:
                values.isDefault === 'true'
                    ? true
                    : values.isDefault === 'false'
                      ? false
                      : '',
            productItem: values.productItem,
            categoryId: categoryId,
        });
    };

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();
    const closeModal = () => {
        setOpenDialog(false);
        reset();
    };

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    useEffect(() => {
        if (isSuccess) {
            refetch();
            closeModal();
        }
    }, [isSuccess]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    ADD NEW ITEM
                </DialogTitle>
                <DialogIconClose handleClose={closeModal} />
            </Box>

            <DialogContent>
                <Box
                    ref={formRef}
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Box display={'flex'} pt={pxToRem(15)}>
                        <Controller
                            name="itemPosition"
                            control={control}
                            rules={{
                                required: 'Position Item is required',
                            }}
                            render={({
                                field: { name, value = '', onChange },
                            }) => (
                                <TextField
                                    fullWidth
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    label="Position Item"
                                    error={Boolean(errors[name])}
                                    helperText={
                                        errors[name]?.message?.toString() || ''
                                    }
                                />
                            )}
                        />

                        <FormControl
                            error={Boolean(errors?.isDefault)}
                            fullWidth
                            sx={{ ml: pxToRem(10) }}
                        >
                            <InputLabel>Is Default</InputLabel>
                            <Controller
                                name="isDefault"
                                control={control}
                                rules={{
                                    required: 'Is Default is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Select
                                        label="Is Default"
                                        name={name}
                                        value={value || ''}
                                        onChange={(e) =>
                                            onChange(String(e.target.value))
                                        }
                                    >
                                        {defaultSelected.map((status) => (
                                            <MenuItem
                                                key={status.value}
                                                value={status.value}
                                            >
                                                {status.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors?.isDefault && (
                                <FormHelperText>
                                    {errors?.isDefault.message?.toString()}
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        pt={pxToRem(15)}
                    >
                        <Controller
                            name="productItem"
                            control={control}
                            render={({ field: { onChange, ref, value } }) => (
                                <Autocomplete
                                    limitTags={5}
                                    disableCloseOnSelect
                                    sx={{ width: '50%' }}
                                    options={dataProductToonTint ?? []}
                                    loading={isFetching}
                                    value={
                                        (dataProductToonTint ?? []).find(
                                            (opt) => opt.id === value,
                                        ) ?? null
                                    }
                                    onChange={(_, newValue) => {
                                        onChange(newValue ? newValue.id : '');
                                        setViewProduct(
                                            newValue ? newValue.id : '',
                                        );
                                    }}
                                    inputValue={searchProduct}
                                    onInputChange={(_, newInput, reason) => {
                                        if (reason !== 'reset') {
                                            setSearchProduct(newInput);
                                        }
                                    }}
                                    getOptionLabel={(option) =>
                                        option.title || ''
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    filterSelectedOptions
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Users"
                                            variant="outlined"
                                            fullWidth
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {isFetching && (
                                                                <CircularProgress
                                                                    color="inherit"
                                                                    size={20}
                                                                />
                                                            )}
                                                            {
                                                                params
                                                                    .InputProps
                                                                    .endAdornment
                                                            }
                                                        </>
                                                    ),
                                                },
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                        {viewProduct && (
                            <Box pl={pxToRem(10)} sx={{ cursor: 'pointer' }}>
                                <Tooltip title="View Product">
                                    <PageviewIcon
                                        fontSize={'large'}
                                        onClick={() => {
                                            setOpenViewProductDialog(true);
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                    disabled={isLoading}
                >
                    Create Item
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={closeModal}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <Dialog
                open={openViewProductDialog}
                onClose={() => setOpenViewProductDialog(false)}
                slotProps={{
                    paper: {
                        sx: {
                            width: '800px',
                            minHeight: '600px',
                            maxWidth: '90%',
                        },
                    },
                }}
            >
                <ViewProduct
                    setOpenViewProductDialog={setOpenViewProductDialog}
                    viewProduct={viewProduct}
                />
            </Dialog>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Created a Card!'}
            />
        </>
    );
};
export default AddItem;
